/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { apiErr } from "@/lib/utils";
import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, ClipboardList, BarChart2, Shield,
  ArrowLeft, RefreshCw, Search, Plus, Pencil, X, CheckCircle,
  Menu, ChevronDown, ChevronUp, ChevronLeft, AlertTriangle, MapPin
} from "lucide-react";
import { VVF_DATA, VVF_REGIONI, getComandi, getSedi } from "../data/vvfData";
import ReportView from "./ReportView";
import confirmDialog from "./confirmDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const createRopApi = (token) => {
  const h = { Authorization: `Bearer ${token}` };
  const base = `${BACKEND_URL}/api/panel`;
  return {
    getStats:              ()         => axios.get(`${base}/stats`, { headers: h }),
    getOperazioni:         ()         => axios.get(`${base}/operazioni`, { headers: h }),
    getTas2:               (search)   => axios.get(`${base}/tas2-personale`, { params: search ? { search } : {}, headers: h }),
    createTas2:            (data)     => axios.post(`${base}/tas2-personale`, data, { headers: h }),
    updateTas2:            (id, data) => axios.put(`${base}/tas2-personale/${id}`, data, { headers: h }),
    deleteTas2:            (id)       => axios.delete(`${base}/tas2-personale/${id}`, { headers: h }),
    getTas2Partecipazioni: (id)       => axios.get(`${base}/tas2/${id}/partecipazioni`, { headers: h }),
    getReport:             (p = {})   => axios.get(`${base}/report`, { params: { ...(p.da ? { da: p.da } : {}), ...(p.a ? { a: p.a } : {}) }, headers: h }),
  };
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROLE_LABELS = { rop: "ROP", ror: "ROR", ron: "RON" };

const scopeBadge = (operatore) => {
  const role = operatore?.ruolo;
  const label = ROLE_LABELS[role] || role?.toUpperCase();
  if (role === "ron") return `${label}: Nazionale`;
  if (role === "ror") return `${label}: Reg. ${operatore?.regione || "–"}`;
  if (role === "rop") return `${label}: Cmd. ${operatore?.comando || "–"}`;
  return label;
};

const fmtDate = (d) => {
  if (!d) return "–";
  try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return d; }
};

// ── Shared sub-components ────────────────────────────────────────────────────

const StatCard = ({ label, value, color = "orange" }) => {
  const colors = {
    orange: "border-orange-500 text-orange-400",
    blue:   "border-blue-500 text-blue-400",
    green:  "border-green-500 text-green-400",
    red:    "border-red-500 text-red-400",
    purple: "border-purple-500 text-purple-400",
  };
  return (
    <div className={`tactical-card p-4 border-l-4 ${colors[color]}`}>
      <div className="text-2xl font-bold text-white">{value ?? "–"}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
};

const StatusBadge = ({ attiva }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${attiva ? "bg-green-500/20 text-green-400 border border-green-500/40" : "bg-red-500/20 text-red-400 border border-red-500/40"}`}>
    {attiva ? "ATTIVA" : "CHIUSA"}
  </span>
);

// ── Filtri (stile Admin) ──────────────────────────────────────────────────────
const FilterBar = ({ onReset, children }) => (
  <div className="tactical-card p-3 mb-4">
    <div className="flex flex-wrap gap-2 items-end">
      {children}
      <button
        className="btn btn-secondary btn-sm py-1 px-3 text-xs flex items-center gap-1 self-end"
        onClick={onReset}
        title="Azzera filtri"
      >
        <X size={12} /> Azzera
      </button>
    </div>
  </div>
);
const FilterInput = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-xs text-slate-400">{label}</label>}
    <div className="relative">
      {type === "text" && <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />}
      <input
        type={type}
        className="form-input py-1 text-xs"
        style={{ minWidth: 140, paddingLeft: type === "text" ? "2rem" : undefined }}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  </div>
);
const FilterSelect = ({ label, value, onChange, options, placeholder = "Tutti" }) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-xs text-slate-400">{label}</label>}
    <select
      className="form-select py-1 text-xs"
      style={{ minWidth: 130 }}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
          {typeof o === "string" ? o : o.label}
        </option>
      ))}
    </select>
  </div>
);

// ── Dashboard Tab ─────────────────────────────────────────────────────────────

const DashboardTab = ({ api }) => {
  const [stats, setStats] = useState(null);
  const [operazioni, setOperazioni] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: ops }] = await Promise.all([api.getStats(), api.getOperazioni()]);
      setStats(s);
      setOperazioni(Array.isArray(ops) ? ops : []);
    } catch { toast.error("Errore caricamento dashboard"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">Panoramica</h2>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Operazioni Totali"  value={stats?.operazioni_totali}  color="orange" />
        <StatCard label="Operazioni Attive"  value={stats?.operazioni_attive}  color="green" />
        <StatCard label="Operazioni Chiuse"  value={stats?.operazioni_chiuse}  color="red" />
        <StatCard label="Soccorritori"        value={stats?.soccorritori_totali} color="blue" />
        <StatCard label="Missioni"            value={stats?.missioni_totali}    color="purple" />
        <StatCard label="Personale TAS2"      value={stats?.tas2_totali}        color="orange" />
      </div>

      <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
        <ClipboardList size={14} className="text-orange-400" /> Interventi recenti
      </h3>
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left p-3 text-slate-400 font-medium">Nome</th>
              <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Località</th>
              <th className="text-left p-3 text-slate-400 font-medium hidden md:table-cell">Regione</th>
              <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
              <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Socc.</th>
              <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Miss.</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {operazioni.slice(0, 10).map((op, i) => (
              <Fragment key={op.id}>
                <tr
                  className={`border-b border-slate-800 hover:bg-slate-800/40 cursor-pointer ${i % 2 === 1 ? "bg-slate-800/25" : ""}`}
                  onClick={() => setExpanded(expanded === op.id ? null : op.id)}
                >
                  <td className="p-3 text-white font-medium">{op.nome}</td>
                  <td className="p-3 text-slate-300 hidden sm:table-cell">{op.localita || "–"}</td>
                  <td className="p-3 text-slate-300 hidden md:table-cell">{op.regione || "–"}</td>
                  <td className="p-3"><StatusBadge attiva={!op.chiusa} /></td>
                  <td className="p-3 text-slate-300 hidden sm:table-cell">{op.num_soccorritori ?? "–"}</td>
                  <td className="p-3 text-slate-300 hidden sm:table-cell">{op.num_missioni ?? "–"}</td>
                  <td className="p-3 text-slate-400">
                    {expanded === op.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </td>
                </tr>
                {expanded === op.id && (
                  <tr className="border-b border-slate-800 bg-slate-900/60">
                    <td colSpan={7} className="p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="text-slate-500">Data apertura: </span><span className="text-slate-200">{fmtDate(op.data_inizio)}</span></div>
                        <div><span className="text-slate-500">Comando VVF: </span><span className="text-slate-200">{op.comando_vvf || "–"}</span></div>
                        <div><span className="text-slate-500">Soccorritori: </span><span className="text-slate-200">{op.num_soccorritori ?? "–"}</span></div>
                        <div><span className="text-slate-500">Missioni: </span><span className="text-slate-200">{op.num_missioni ?? "–"}</span></div>
                        {op.localita && <div className="col-span-2"><span className="text-slate-500">Località: </span><span className="text-slate-200">{op.localita}</span></div>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {operazioni.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">Nessun intervento disponibile</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Interventi Tab ────────────────────────────────────────────────────────────

const InterventiTab = ({ api }) => {
  const [operazioni, setOperazioni] = useState([]);
  const [search, setSearch] = useState("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA]   = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getOperazioni();
      setOperazioni(Array.isArray(data) ? data : []);
    } catch { toast.error("Errore caricamento interventi"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = operazioni.filter(op => {
    const q = search.toLowerCase();
    const matchTesto = !q || (op.nome || "").toLowerCase().includes(q) || (op.localita || "").toLowerCase().includes(q);
    const d = (op.data_inizio || "").slice(0, 10); // YYYY-MM-DD per confronto stringa
    const matchDa = !dataDa || (d && d >= dataDa);
    const matchA  = !dataA  || (d && d <= dataA);
    return matchTesto && matchDa && matchA;
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white">Interventi</h2>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
      </div>
      <FilterBar onReset={() => { setSearch(""); setDataDa(""); setDataA(""); }}>
        <FilterInput label="Cerca" value={search} onChange={setSearch} placeholder="Nome, località…" />
        <FilterInput label="Da data" type="date" value={dataDa} onChange={setDataDa} />
        <FilterInput label="A data" type="date" value={dataA} onChange={setDataA} />
      </FilterBar>
      <div className="text-xs text-slate-400 mb-3">{filtered.length} interventi</div>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-3 text-slate-400 font-medium">Nome</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Località</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden md:table-cell">Regione</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden lg:table-cell">Comando VVF</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Socc.</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Miss.</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden md:table-cell">Apertura</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((op, i) => (
                <tr key={op.id} className={`border-b border-slate-800 hover:bg-slate-800/40 ${i % 2 === 1 ? "bg-slate-800/25" : ""}`}>
                  <td className="p-3 text-white font-medium">{op.nome}</td>
                  <td className="p-3 text-slate-300 hidden sm:table-cell">{op.localita || "–"}</td>
                  <td className="p-3 text-slate-300 hidden md:table-cell">{op.regione || "–"}</td>
                  <td className="p-3 text-slate-300 hidden lg:table-cell">{op.comando_vvf || "–"}</td>
                  <td className="p-3"><StatusBadge attiva={!op.chiusa} /></td>
                  <td className="p-3 text-slate-300 hidden sm:table-cell">{op.num_soccorritori ?? "–"}</td>
                  <td className="p-3 text-slate-300 hidden sm:table-cell">{op.num_missioni ?? "–"}</td>
                  <td className="p-3 text-slate-300 hidden md:table-cell">{fmtDate(op.data_inizio)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Nessun intervento trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Storico Partecipazioni Modal ───────────────────────────────────────────────

const StoricoModal = ({ api, personaId, personaNome, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.getTas2Partecipazioni(personaId);
        setData(res);
      } catch { toast.error("Errore caricamento storico"); onClose(); }
      finally { setLoading(false); }
    })();
  }, [api, personaId, onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="tactical-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 p-6 pb-0">
          <div>
            <h3 className="text-white font-bold">Storico Ricerche</h3>
            <p className="text-xs text-slate-400 mt-0.5">{personaNome}</p>
          </div>
          <button className="text-slate-400 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 pt-4">
          {loading ? (
            <div className="text-slate-400 text-sm text-center py-8">Caricamento...</div>
          ) : data?.partecipazioni?.length > 0 ? (
            <div className="space-y-3">
              {data.partecipazioni.map((p, i) => (
                <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-white font-medium text-sm">
                        {p.operazione_nome}
                        {p.operazione_localita && (
                          <span className="text-slate-400 font-normal"> — {p.operazione_localita}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin size={11} />{p.regione || "–"}</span>
                        <span>{p.turno_nome || "–"}</span>
                        <span>{fmtDate(p.data_inizio)}{p.data_fine ? ` → ${fmtDate(p.data_fine)}` : ""}</span>
                      </div>
                    </div>
                    {p.ruolo_turno && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/40 shrink-0">
                        {p.ruolo_turno}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <AlertTriangle size={32} className="mx-auto mb-3 text-slate-600" />
              <p className="text-slate-500 text-sm">Nessuna ricerca registrata</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── TAS2 Form Modal ────────────────────────────────────────────────────────────

const EMPTY_TAS2 = { cognome: "", nome: "", qualifica: "", regione: "", comando: "", sede: "", turno: "", salto: "", telefono: "", email: "", tas2_dal: "", attivo: true };

/** Abbina una sede abbreviata (es. "Castellammare d.S.") alla voce esatta in
 *  opzioni usando word-prefix matching. Identico ad AdminPanel. */
const matchSede = (stored, opzioni) => {
  if (!stored) return "";
  if (opzioni.includes(stored)) return stored;
  const exactCI = opzioni.find(o => o.toLowerCase() === stored.toLowerCase());
  if (exactCI) return exactCI;
  const sub = opzioni.find(o => o.toLowerCase().includes(stored.toLowerCase()));
  if (sub) return sub;
  const norm = s => s.replace(/\./g, "").toLowerCase().split(/\s+/).filter(Boolean);
  const sw = norm(stored);
  const prefix = opzioni.find(opt => {
    const ow = norm(opt);
    return ow.length >= sw.length && sw.every((w, i) => ow[i] && ow[i].startsWith(w));
  });
  return prefix || stored;
};

/** Normalizza i campi geografici di un record TAS2 verso le voci canoniche
 *  (regione case-insensitive su VVF_REGIONI, comando uppercase, sede prefix-match)
 *  così i <select> a cascata combaciano in modifica. Identico ad AdminPanel. */
const normalizeTas2Geo = (r) => {
  let regioneVal = r.regione || "";
  if (regioneVal) {
    const match = VVF_REGIONI.find(reg => reg.toLowerCase() === regioneVal.toLowerCase());
    if (match) regioneVal = match;
  }
  const comandoVal = (r.comando || "").toUpperCase();
  if (!regioneVal && comandoVal) {
    const found = Object.entries(VVF_DATA).find(([, cmds]) => Object.keys(cmds).includes(comandoVal));
    if (found) regioneVal = found[0];
  }
  const sedeVal = matchSede(r.sede || "", getSedi(regioneVal, comandoVal));
  return { regione: regioneVal, comando: comandoVal, sede: sedeVal };
};

const Tas2Modal = ({ mode, initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial || EMPTY_TAS2);
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!form.cognome.trim() || !form.nome.trim()) { toast.error("Cognome e Nome sono obbligatori"); return; }
    setSaving(true);
    const payload = { ...form, salto: form.salto !== "" ? Number(form.salto) : null, tas2_dal: form.tas2_dal !== "" ? Number(form.tas2_dal) : null };
    await onSave(payload, () => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="tactical-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold">{mode === "create" ? "Aggiungi Personale TAS2" : "Modifica Personale TAS2"}</h3>
          <button className="text-slate-400 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1"><label className="form-label">Cognome *</label><input className="form-input" value={form.cognome} onChange={f("cognome")} placeholder="ROSSI" /></div>
          <div className="col-span-2 sm:col-span-1"><label className="form-label">Nome *</label><input className="form-input" value={form.nome} onChange={f("nome")} placeholder="Mario" /></div>
          <div><label className="form-label">Qualifica</label><input className="form-input" value={form.qualifica} onChange={f("qualifica")} placeholder="VF, VE, CS…" /></div>
          <div>
            <label className="form-label">Regione</label>
            <select className="form-input" value={form.regione}
              onChange={e => setForm(p => ({ ...p, regione: e.target.value, comando: "", sede: "" }))}>
              <option value="">— seleziona —</option>
              {VVF_REGIONI.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Comando</label>
            <select className="form-input" value={form.comando}
              onChange={e => setForm(p => ({ ...p, comando: e.target.value, sede: "" }))}>
              <option value="">— seleziona —</option>
              {getComandi(form.regione).filter(c => c !== "").map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Sede</label>
            <select className="form-input" value={form.sede} onChange={f("sede")}>
              <option value="">— seleziona —</option>
              {getSedi(form.regione, form.comando).map(s => <option key={s} value={s}>{s}</option>)}
              {form.sede && !getSedi(form.regione, form.comando).includes(form.sede) && (
                <option value={form.sede}>{form.sede}</option>
              )}
            </select>
          </div>
          <div><label className="form-label">Turno</label><input className="form-input" value={form.turno} onChange={f("turno")} placeholder="A, B, C, D…" /></div>
          <div><label className="form-label">Salto</label><input className="form-input" type="number" min="1" max="8" value={form.salto} onChange={f("salto")} placeholder="1–8" /></div>
          <div><label className="form-label">Telefono</label><input className="form-input" value={form.telefono} onChange={f("telefono")} placeholder="3xx…" /></div>
          <div><label className="form-label">TAS2 dal</label><input className="form-input" type="number" min="2000" max="2099" value={form.tas2_dal} onChange={f("tas2_dal")} placeholder="2019" /></div>
          <div className="col-span-2"><label className="form-label">Email istituzionale</label><input className="form-input" type="email" value={form.email} onChange={f("email")} placeholder="mario.rossi@vigilfuoco.it" /></div>
          <div className="col-span-2 flex items-center gap-2">
            <input id="tas2-attivo" type="checkbox" checked={form.attivo} onChange={f("attivo")} className="w-4 h-4 accent-orange-500" />
            <label htmlFor="tas2-attivo" className="text-sm text-slate-300 cursor-pointer">Attivo (autorizzato alla registrazione)</label>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-slate-700">
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Salvataggio…" : mode === "create" ? "Aggiungi" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
};

// ── Personale TAS2 Tab ────────────────────────────────────────────────────────

const Tas2Tab = ({ api }) => {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [filtroRegione, setFiltroRegione] = useState("");
  const [filtroComando, setFiltroComando] = useState("");
  const [filtroTurno, setFiltroTurno]     = useState("");
  const [filtroStato, setFiltroStato]     = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // null | { mode, data }
  const [storicoId, setStoricoId] = useState(null);
  const [storicoNome, setStoricoNome] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getTas2(search.trim() || undefined);
      setRecords(Array.isArray(data) ? data : []);
    } catch { toast.error("Errore caricamento personale TAS2"); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ mode: "create", data: null });
  const openEdit = (r) => {
    const geo = normalizeTas2Geo(r);
    setModal({
      mode: "edit", id: r.id,
      data: { cognome: r.cognome || "", nome: r.nome || "", qualifica: r.qualifica || "", regione: geo.regione, comando: geo.comando, sede: geo.sede, turno: r.turno || "", salto: r.salto ?? "", telefono: r.telefono || "", email: r.email || "", tas2_dal: r.tas2_dal ?? "", attivo: r.attivo !== false },
    });
  };

  const handleSave = async (payload, onDone) => {
    try {
      if (modal.mode === "create") { await api.createTas2(payload); toast.success("Personale TAS2 aggiunto"); }
      else { await api.updateTas2(modal.id, payload); toast.success("Personale TAS2 aggiornato"); }
      setModal(null); load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); onDone(); }
  };

  const handleDelete = async (id, nome) => {
    if (!(await confirmDialog({ title: "Elimina dalla whitelist", message: `Eliminare "${nome}" dalla whitelist TAS2?`, danger: true, confirmLabel: "Elimina" }))) return;
    try { await api.deleteTas2(id); toast.success("Record eliminato"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  // Opzioni filtro derivate dai record visibili (già scopati lato server per ruolo).
  // Regione/Comando si mostrano solo se c'è più di un valore (per ROP è uno solo).
  const regioniDisp = [...new Set(records.map(r => (r.regione || "").trim()).filter(Boolean))].sort();
  const comandiDisp = [...new Set(
    records.filter(r => !filtroRegione || (r.regione || "").toLowerCase() === filtroRegione.toLowerCase())
           .map(r => (r.comando || "").trim()).filter(Boolean)
  )].sort();
  const turniDisp = [...new Set(records.map(r => (r.turno || "").trim()).filter(Boolean))].sort();

  const recordsFiltrati = records.filter(r =>
    (!filtroRegione || (r.regione || "").toLowerCase() === filtroRegione.toLowerCase()) &&
    (!filtroComando || (r.comando || "").toUpperCase() === filtroComando.toUpperCase()) &&
    (!filtroTurno || (r.turno || "").trim() === filtroTurno) &&
    (!filtroStato || (filtroStato === "attivo" ? r.attivo !== false : r.attivo === false))
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-white">Personale TAS2</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
          <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={14} /> Aggiungi</button>
        </div>
      </div>
      <FilterBar onReset={() => { setSearch(""); setFiltroRegione(""); setFiltroComando(""); setFiltroTurno(""); setFiltroStato(""); }}>
        <FilterInput label="Cerca" value={search} onChange={setSearch} placeholder="Nome, email…" />
        {regioniDisp.length > 1 && (
          <FilterSelect label="Regione" value={filtroRegione} onChange={v => { setFiltroRegione(v); setFiltroComando(""); }}
            options={regioniDisp} placeholder="Tutte le regioni" />
        )}
        <FilterSelect label="Comando" value={filtroComando} onChange={setFiltroComando}
          options={comandiDisp} placeholder="Tutti i comandi" />
        <FilterSelect label="Turno" value={filtroTurno} onChange={setFiltroTurno}
          options={turniDisp} placeholder="Tutti i turni" />
        <FilterSelect label="Stato" value={filtroStato} onChange={setFiltroStato}
          options={[{ value: "attivo", label: "Attivo" }, { value: "inattivo", label: "Inattivo" }]}
          placeholder="Tutti" />
      </FilterBar>
      <div className="text-xs text-slate-400 mb-3">{recordsFiltrati.length} record{filtroRegione || filtroComando || filtroTurno || filtroStato ? ` (${records.length} totali)` : ""}</div>

      {loading ? <div className="text-slate-400 text-sm">Caricamento...</div> : (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-3 text-slate-400 font-medium">Cognome Nome</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden sm:table-cell">Qualifica</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden md:table-cell">Regione</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden md:table-cell">Comando</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden lg:table-cell">Turno</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden lg:table-cell">Telefono</th>
                <th className="text-left p-3 text-slate-400 font-medium hidden lg:table-cell">Email</th>
                <th className="text-left p-3 text-slate-400 font-medium">Stato</th>
                <th className="text-left p-3 text-slate-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {recordsFiltrati.map((r, i) => (
                <tr key={r.id} className={`border-b border-slate-800 hover:bg-slate-800/40 ${i % 2 === 1 ? "bg-slate-800/25" : ""}`}>
                  <td className="p-3 text-white font-medium">{r.cognome} {r.nome}</td>
                  <td className="p-3 text-slate-300 text-xs hidden sm:table-cell">{r.qualifica || "–"}</td>
                  <td className="p-3 text-slate-300 text-xs hidden md:table-cell">{r.regione || "–"}</td>
                  <td className="p-3 text-slate-300 text-xs hidden md:table-cell">{r.comando || "–"}</td>
                  <td className="p-3 text-slate-300 text-xs hidden lg:table-cell">{r.turno || "–"}{r.salto ? `/${r.salto}` : ""}</td>
                  <td className="p-3 text-slate-300 text-xs font-mono hidden lg:table-cell">{r.telefono || "–"}</td>
                  <td className="p-3 text-xs font-mono hidden lg:table-cell">
                    {r.email ? <span className="text-blue-400">{r.email}</span> : <span className="text-slate-500 italic">n/d</span>}
                  </td>
                  <td className="p-3">
                    {r.attivo
                      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/40">Attivo</span>
                      : <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/40">Inattivo</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      <button className="btn btn-secondary btn-sm" title="Modifica" onClick={() => openEdit(r)}><Pencil size={13} /></button>
                      <button
                        className="btn btn-secondary btn-sm text-xs"
                        title="Storico ricerche"
                        onClick={() => { setStoricoId(r.id); setStoricoNome(`${r.cognome} ${r.nome}`); }}
                      >
                        Storico
                      </button>
                      <button
                        className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40"
                        title="Elimina"
                        onClick={() => handleDelete(r.id, `${r.cognome} ${r.nome}`)}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {recordsFiltrati.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-slate-500">Nessun record trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Tas2Modal
          mode={modal.mode}
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {storicoId && (
        <StoricoModal
          api={api}
          personaId={storicoId}
          personaNome={storicoNome}
          onClose={() => { setStoricoId(null); setStoricoNome(""); }}
        />
      )}
    </div>
  );
};

// ── Report Tab ────────────────────────────────────────────────────────────────
// Grafici e layout del report: spostati nel componente condiviso ReportView.
const ReportTab = ({ api, scopeType, logoBase64 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (periodo) => {
    setLoading(true);
    try { const { data: res } = await api.getReport(periodo); setData(res); }
    catch { toast.error("Errore caricamento report"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Solo al PRIMO caricamento: sostituire il report a ogni refetch smonterebbe
  // ReportView e con esso lo stato del filtro (le date selezionate sparivano).
  if (loading && !data) return <div className="text-slate-400 text-sm text-center py-20">Caricamento report…</div>;
  if (!data) return <div className="text-red-400 text-sm text-center py-20">Errore nel caricamento dei dati.</div>;

  const isNational = scopeType === "nazionale";

  return (
    <div className="max-w-5xl mx-auto">
      <ReportView
        data={data}
        title="Report Generale"
        subtitle="Riepilogo aggregato del proprio ambito di competenza"
        onRefresh={load}
        loading={loading}
        logoBase64={logoBase64}
        isNational={isNational}
      />
    </div>
  );
};

// ── ROP/ROR/RON Panel ──────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard",  label: "Dashboard",     icon: LayoutDashboard },
  { id: "operazioni", label: "Interventi",     icon: ClipboardList },
  { id: "tas2",       label: "Personale TAS2", icon: Users },
  { id: "report",     label: "Report",         icon: BarChart2 },
];

const ALLOWED_ROLES = ["rop", "ror", "ron"];

const RopPanel = ({ operatore, token, onBack, logoBase64 }) => {
  const [tab, setTab] = useState("dashboard");
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const api = createRopApi(token);
  const role = operatore?.ruolo;

  if (!ALLOWED_ROLES.includes(role)) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="tactical-card p-8 max-w-md text-center">
          <Shield size={48} className="mx-auto mb-4 text-orange-500" />
          <h2 className="text-xl font-bold text-white mb-2">Accesso non autorizzato</h2>
          <p className="text-slate-400 mb-6 text-sm">
            Questo pannello è riservato ai ruoli ROP, ROR e RON.
          </p>
          <button className="btn btn-secondary w-full" onClick={onBack}>
            <ArrowLeft size={14} /> Torna all'app
          </button>
        </div>
      </div>
    );
  }

  const scopeType = operatore?.ruolo === "ron" ? "nazionale" : operatore?.ruolo === "ror" ? "regionale" : "provinciale";

  const ActiveTab = { dashboard: DashboardTab, operazioni: InterventiTab, tas2: Tas2Tab, report: ReportTab }[tab];

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header — uniformato allo stile Admin */}
      <header className="bg-slate-900 border-b border-slate-800 px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-4">
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-300 hover:bg-slate-800"
          onClick={() => setSidebarMobileOpen(true)}
          aria-label="Apri menu"
        >
          <Menu size={20} />
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>
          <ArrowLeft size={14} /> <span className="hidden sm:inline">App</span>
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {logoBase64 && (
            <img src={logoBase64} alt="Logo" className="w-7 h-7 object-contain rounded" />
          )}
          <span className="text-white font-bold text-sm sm:text-base truncate">Pannello {ROLE_LABELS[role] || role?.toUpperCase()}</span>
        </div>
        <div className="ml-auto text-xs text-slate-400 hidden sm:block truncate">{operatore?.email}</div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile backdrop */}
        {sidebarMobileOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-[2px]"
            onClick={() => setSidebarMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`bg-slate-900 border-r border-slate-800 flex-shrink-0 fixed md:static inset-y-0 left-0 z-50 ${sidebarCollapsed ? "w-14" : "w-48"} transform transition-all duration-200 ease-out ${sidebarMobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
          <div className="hidden md:flex justify-end px-2 pt-2">
            <button
              type="button"
              onClick={() => setSidebarCollapsed(c => !c)}
              title={sidebarCollapsed ? "Espandi menu" : "Comprimi menu"}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <ChevronLeft size={14} className={`transition-transform duration-200 ${sidebarCollapsed ? "rotate-180" : ""}`} />
            </button>
          </div>
          <nav className="p-3 space-y-1">
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setSidebarMobileOpen(false); }}
                  title={t.label}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${sidebarCollapsed ? "justify-center" : "justify-start"} ${isActive ? "bg-orange-500/20 text-orange-400 font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  {!sidebarCollapsed && <span className="truncate">{t.label}</span>}
                </button>
              );
            })}
          </nav>
          {!sidebarCollapsed && (
            <div className="absolute bottom-4 left-0 right-0 px-3">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2 text-center">
                <div className="text-xs font-bold text-orange-400">{ROLE_LABELS[role] || role?.toUpperCase()}</div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  {scopeType === "nazionale" ? "Ambito nazionale" : scopeType === "regionale" ? (operatore?.regione || "–") : (operatore?.comando || "–")}
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-6">
          <ActiveTab api={api} scopeType={scopeType} logoBase64={logoBase64} />
        </main>
      </div>
    </div>
  );
};

export default RopPanel;
